<script setup lang="ts">
import PageContainer from "~/components/ui/PageContainer.vue";
import ExerciseMCQ from "~/features/computer/components/ExerciseMCQ.vue";
import { useExercises } from "~/features/computer/composables/useExercises";
import { buildComputerCollectionSchema } from "~/features/computer/data/seo/computer-schema";
import { pythonInteractiveCards } from "~/features/python/data/chapter2-interactive";
import { computerSeoConfig } from "~/config/seo.config.computer";

const operatorExercises = useExercises("operators");

const practiceLinks = [
  { title: "Python Notes", to: "/11th/computer/python/notes/", tone: "bg-sky-500 text-white" },
  { title: "Python MCQs", to: "/11th/computer/python/mcqs/", tone: "bg-emerald-500 text-white" },
  { title: "Short Questions", to: "/11th/computer/python/short-questions/", tone: "bg-amber-400 text-slate-950" },
  { title: "Long Questions", to: "/11th/computer/python/long-questions/", tone: "bg-slate-950 text-white" },
];

usePageSeo({
  title: computerSeoConfig.titleTemplate("Chapter 2 Python Exercise"),
  description: "11th class Python chapter ke important practice links aur quick revision Roman Urdu mein.",
  keywords: "1st year python exercise, 11th computer exercise, python practice",
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th Computer", item: "/11th/computer/" },
    { name: "Ch 2 Python", item: "/11th/computer/python/" },
    { name: "Exercise", item: "/11th/computer/python/exercise/" },
  ],
  schemaType: "CollectionPage",
  schemaProperties: buildComputerCollectionSchema({
    about: "Chapter 2 Python Exercise",
    items: [
      { name: "Chapter 2 Python", path: "/11th/computer/python/" },
      { name: "Chapter 2 Python MCQs", path: "/11th/computer/python/mcqs/" },
      { name: "Chapter 2 Python Short Questions", path: "/11th/computer/python/short-questions/" },
      { name: "Chapter 2 Python Long Questions", path: "/11th/computer/python/long-questions/" },
      { name: "Important Questions", path: "/11th/computer/important-questions/" },
    ],
  }),
});
</script>

<template>
  <PageContainer>
    <div class="space-y-8">
      <section class="space-y-4 rounded-3xl bg-slate-950 p-6 text-white">
        <h1 class="text-2xl font-bold tracking-tight">Chapter 2 Python Exercise</h1>
        <p class="text-sm text-slate-200">
          Chapter 2 Python ke important practice links aur quick revision yahan se start karein.
        </p>

        <div class="grid gap-3">
          <NuxtLink
            v-for="item in practiceLinks"
            :key="item.to"
            :to="item.to"
            class="rounded-2xl p-4"
            :class="item.tone"
          >
            <h2 class="text-sm font-semibold">{{ item.title }}</h2>
          </NuxtLink>
        </div>
      </section>

      <section class="space-y-4 rounded-3xl border border-slate-200 bg-white p-5">
        <div class="flex items-center justify-between gap-4">
          <div>
            <h2 class="text-lg font-semibold text-slate-900">Quick Practice</h2>
            <p class="mt-1 text-sm text-slate-600">1 ya 2 sawal yahin solve karo.</p>
          </div>
          <NuxtLink
            to="/11th/computer/python/mcqs/"
            class="shrink-0 rounded-full bg-slate-950 px-3 py-1 text-xs font-medium text-white"
          >
            Full Python MCQs
          </NuxtLink>
        </div>

        <ExerciseMCQ
          v-for="question in operatorExercises.mcqs.slice(0, 2)"
          :key="question.id"
          :question="question"
        />
      </section>

      <section class="rounded-3xl border border-slate-200 bg-slate-50 p-5">
        <h2 class="text-lg font-semibold text-slate-900">Quick Revision Flow</h2>
        <ol class="mt-3 space-y-2 text-sm text-slate-700">
          <li>1. Notes</li>
          <li>2. MCQs</li>
          <li>3. Short Questions</li>
          <li>4. Long Questions</li>
        </ol>
      </section>

      <section class="rounded-3xl border border-slate-200 bg-white p-5">
        <div>
          <h2 class="text-lg font-semibold text-slate-900">More Python Topics</h2>
          <p class="mt-1 text-sm text-slate-600">Agar kisi concept me weakness ho to neeche wala topic page kholo.</p>
        </div>
        <div class="mt-4 grid gap-3 md:grid-cols-2">
          <NuxtLink
            v-for="item in pythonInteractiveCards"
            :key="item.to"
            :to="item.to"
            class="rounded-2xl border border-slate-200 bg-slate-50 p-4 transition hover:border-sky-300 hover:bg-sky-50"
          >
            <h3 class="text-sm font-semibold text-slate-900">{{ item.title }}</h3>
            <p class="mt-2 text-sm leading-6 text-slate-600">{{ item.description }}</p>
          </NuxtLink>
        </div>
      </section>
    </div>
  </PageContainer>
</template>
